<template>
  <div class="container">
    <div class="dropdowns-wrapper">
      <div class="dropdown-container">
        <div class="dropdown-item" style="position: relative;">
          <label for="startDatePicker">Allocation Date <span class="required">*</span></label>
          <div v-if="items4.length > 0" class="lock-overlay" @click.stop="showRestrictionError('date')"></div>

          <input
              type="date"
              v-model="currentDate"
              :min="minDate"
              class="date-box"
              :disabled="items4.length > 0"
          />
        </div>
      </div>

      <div class="dropdown-container">
        <div class="dropdown-item" style="position: relative;">
          <label for="fsm-select">Sales Type <span class="required">*</span></label>
          <div v-if="items4.length > 0 || isSalesTypeDisabled" class="lock-overlay" @click.stop="showRestrictionError('salesType')"></div>

          <select
              id="fsm-select"
              v-model="stockAllocation.sales_type"
              @change="onSalesTypeChange"
              :disabled="isSalesTypeDisabled || items4.length > 0"
          >
            <option value="" disabled selected>Select Sales Type</option>
            <option v-for="type in salesTypes" :key="type.id" :value="type.id">{{ type.types }}</option>
          </select>
        </div>
      </div>

      <div class="dropdown-container" v-if="stockAllocation.sales_type === '2'">
        <div class="dropdown-item" style="position: relative;">
          <label for="dealer-select">Dealer <span class="required">*</span></label>
          <div v-if="items4.length > 0" class="lock-overlay" @click.stop="showRestrictionError('dealer')"></div>

          <select
              id="dealer-select"
              v-model="stockAllocation.dealer_id"
              :disabled="items4.length > 0"
          >
            <option value="" disabled selected>Select Dealer</option>
            <option v-for="dealer in dealers" :key="dealer.profile_id" :value="dealer.profile_id">{{ dealer.name }}</option>
          </select>
        </div>
      </div>

      <div class="dropdown-container" v-if="stockAllocation.sales_type === '1'">
        <div class="dropdown-item" style="position: relative;">
          <label for="sales-rep-select">Sales Rep <span class="required">*</span></label>
          <div v-if="items4.length > 0" class="lock-overlay" @click.stop="showRestrictionError('salesRep')"></div>

          <select
              id="sales-rep-select"
              v-model="stockAllocation.sales_rep_id"
              :disabled="items4.length > 0"
          >
            <option value="" disabled selected>Select Sales Rep</option>
            <option v-for="rep in salesReps" :key="rep.profile_id" :value="rep.profile_id">{{ rep.name }}</option>
          </select>
        </div>
      </div>
    </div>

    <div class="dropdowns-wrapper">
      <div class="dropdown-container">
        <div class="dropdown-item">
          <label for="item-select">Item Name <span class="required">*</span></label>
          <select id="item-select" v-model="selectedItemName">
            <option value="" disabled selected>Select Item</option>
            <option v-for="item in availableItems" :key="item.id" :value="item.id">{{ item.item_name }}</option>
          </select>
        </div>
      </div>

      <div class="dropdown-container">
        <div class="dropdown-item">
          <label for="quantity-input">Item Quantity <span class="required">*</span></label>
          <input type="number" id="quantity-input" v-model="itemQuantity" placeholder="Enter quantity" min="0" @keydown.enter="addItem"/>
        </div>
      </div>

      <div class="dropdown-container">
        <div class="dropdown-item">
          <button type="button" class="btn button-default mt-4" @click="addItem">Add</button>
        </div>
      </div>
    </div>

    <div class="col-sm-12 mt-3">
      <div class="card">
        <div class="card-block row">
          <div class="col-sm-12 col-lg-12 col-xl-12">

            <!-- Empty state -->
            <div v-if="items4.length === 0" class="items-empty">
              <i class="fa fa-inbox"></i>
              <p>No items added yet</p>
              <span>Select an item and quantity above, then click Add.</span>
            </div>

            <!-- Items table -->
            <table v-else class="table">
              <thead>
              <tr>
                <th>Item Name</th>
                <th>Quantity</th>
                <th>Actions</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, index) in items4" :key="index">
                <td>{{ getItemNameById(item.item_id) }}</td>
                <td>{{ parseInt(item.quantity) }}</td>
                <td>
                  <i class="fa fa-edit" @click="editItem(index)"></i>
                  <i class="fa fa-trash" @click="deleteItem(index)"></i>
                </td>
              </tr>
              </tbody>
            </table>

          </div>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <div class="modal-footer-left">
        <div class="temp-allocation-check">
          <input
            type="checkbox"
            id="temp-allocation-checkbox"
            v-model="isTemp"
          />
          <label for="temp-allocation-checkbox">
            Temporary Stock Allocation
          </label>
        </div>
      </div>

      <div class="modal-footer-right">
        <button type="button" class="btn btn-success" @click="updateStockAllocationData" :disabled="updateLoading || loading">
          <i v-if="updateLoading" class="fa fa-spinner fa-spin mr-2"></i>
          {{ updateLoading ? 'Updating...' : 'Update' }}
        </button>
        <button type="button" class="btn btn-primary" v-if="showDispatchButton" @click="navigateToDispatch">Dispatch</button>
      </div>
    </div>

    <edit-modal
        v-if="showEditModal"
        :show="showEditModal"
        :itemName="editItemName"
        :quantity="parseInt(editItemQuantity)"
        :items="masterItems"
        @close="closeEditModal"
        @update="updateItem"
    ></edit-modal>

    <div v-if="showDeleteModal" class="modal">
      <div class="modal-content-confirmation">
        <span class="close" @click="closeDeleteModal">&times;</span>
        <p>Are you sure you want to delete this item?</p>
        <div class="button-container">
          <button type="button" class="btn btn-danger" @click="confirmDeleteItem">Delete</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { fetchStockAllocationById, fetchSalesType, fetchSalesReps, fetchDealerTypeList, fetchItemNameList, updateStockAllocation, fetchOpenStockByItemId } from '@/services/dataService';
import EditModal from '@/pages/Modal/editStockAllocation.vue';
import { useToast } from 'vue-toastification';

const toast = useToast();
const route = useRoute();
const router = useRouter();

const stockAllocation = ref({});
const loading = ref(false);
const salesTypes = ref([]);
const salesReps = ref([]);
const dealers = ref([]);
const localToken = ref(localStorage.getItem('token_set') || '');
const profileId = ref(localStorage.getItem('profile_id') || '');
const reportingProfileId = ref(localStorage.getItem('reportingL1ProfileId') || '');
const userCategory = ref(localStorage.getItem('user_label') || '');
const items4 = ref([]); // Items currently in the table
const masterItems = ref([]); // All items from API
const availableItems = ref([]); // Items for dropdown (masterItems - items4)

const showEditModal = ref(false);
const editItemName = ref('');
const editItemQuantity = ref(0);
const editIndex = ref(-1);
const selectedItemName = ref('');
const itemQuantity = ref(0);

const isSalesTypeDisabled = ref(true);
const showDeleteModal = ref(false);
const showDispatchButton = ref(false);
const deleteIndex = ref(null);
const updateLoading = ref(false);
const isTemp = ref(false);

const currentDate = ref('');
const minDate = ref('');

const showRestrictionError = (field) => {
  if (field === 'date') toast.error("Cannot change the allocation date once items are added.");
  else if (field === 'salesRep') toast.error("Cannot change the sales rep once items are added.");
  else if (field === 'dealer') toast.error("Cannot change the dealer once items are added.");
  else if (field === 'salesType') toast.error("Cannot change the sales type on an existing record.");
};

// CRITICAL: Maintains API order and removes selected items
const refreshAvailableItems = () => {
  const selectedIds = items4.value.map(i => i.item_id);
  availableItems.value = masterItems.value.filter(item => !selectedIds.includes(item.id));
};

const fetchStockAllocationData = async () => {
  try {
    loading.value = true;
    const id = route.params.id;
    const response = await fetchStockAllocationById(id, localToken.value);
    if (response.data.status === 'success') {
      stockAllocation.value = response.data.data.stock_allocation;
      isTemp.value = stockAllocation.value.is_temp === '1' || stockAllocation.value.is_temp === 1;
      currentDate.value = stockAllocation.value.stock_allocation_date;
      minDate.value = stockAllocation.value.stock_allocation_date;

      await fetchSalesTypes();
      if (stockAllocation.value.sales_type === '1') await fetchSalesRepsData();
      else if (stockAllocation.value.sales_type === '2') await fetchDealers();

      items4.value = response.data.data.items.map(item => ({
        item_id: item.item_id,
        quantity: item.qty
      }));

      refreshAvailableItems();
    }
  } catch (error) {
    toast.error('Error fetching data');
  } finally {
    loading.value = false;
  }
};

const fetchSalesTypes = async () => {
  const companyId = localStorage.getItem('company_id');
  const response = await fetchSalesType(companyId, localToken.value);
  if (response.data.status === 'success') salesTypes.value = response.data.data;
};

const fetchSalesRepsData = async () => {
  const profileId = localStorage.getItem('profile_id');
  const distributorId = userCategory.value === 'DISTRIBUTOR BACK OFFICE' ? reportingProfileId.value : profileId;
  const response = await fetchSalesReps(profileId, distributorId, localToken.value);
  if (response?.data?.status === 'success' || response?.data?.data) {
    salesReps.value = response.data.data;
  }
};

const fetchDealers = async () => {
  const profileId = localStorage.getItem('profile_id');
  const response = await fetchDealerTypeList(profileId, localToken.value);
  if (response.data.status === 'success') dealers.value = response.data.data;
};

const fetchItems = async () => {
  const companyId = localStorage.getItem('company_id');
  const response = await fetchItemNameList(companyId, 1, localToken.value);
  if (response.data.status === 'success') {
    masterItems.value = response.data.data;
    refreshAvailableItems();
  }
};

const getItemNameById = (itemId) => {
  const item = masterItems.value.find(item => item.id === itemId);
  return item ? item.item_name : 'Unknown Item';
};

const addItem = async () => {
  if (!selectedItemName.value || itemQuantity.value <= 0) {
    toast.error("Please select an item and valid quantity.");
    return;
  }
  if (!Number.isInteger(Number(itemQuantity.value))) {
    toast.error("Quantity must be a whole number.");
    return;
  }

  try {
    const distributorId = localStorage.getItem('profile_id');
    const response = await fetchOpenStockByItemId(selectedItemName.value, distributorId, localToken.value);
    const openStockData = response.data.data;
    
    if (!openStockData) {
      toast.error("Could not retrieve stock information for validation.");
      return;
    }
    if (parseInt(openStockData.is_active) !== 1) {
      toast.error("This item is currently inactive and cannot be allocated.");
      return;
    }
    if (parseFloat(itemQuantity.value) > parseFloat(openStockData.open_stock)) {
      toast.error(`Quantity exceeds available open stock (${openStockData.open_stock}).`);
      return;
    }
  } catch (error) {
    console.error('Error fetching open stock:', error);
    toast.error("An error occurred while validating stock.");
    return;
  }

  const existingItem = items4.value.find(item => item.item_id === selectedItemName.value);
  if (existingItem) {
    toast.error("Item already in list.");
  } else {
    items4.value.push({
      item_id: selectedItemName.value,
      quantity: itemQuantity.value
    });
    selectedItemName.value = '';
    itemQuantity.value = 0;
    refreshAvailableItems();
  }
};

const editItem = (index) => {
  editIndex.value = index;
  editItemName.value = items4.value[index].item_id;
  editItemQuantity.value = items4.value[index].quantity;
  showEditModal.value = true;
};

const updateItem = async (updatedItem) => {
  if (updatedItem.quantity <= 0) {
    toast.error("Invalid quantity.");
    return;
  }
  
  if (editIndex.value !== -1) {
    try {
      const distributorId = localStorage.getItem('profile_id');
      const response = await fetchOpenStockByItemId(editItemName.value, distributorId, localToken.value);
      const openStockData = response.data.data;
      
      if (!openStockData) {
        toast.error("Could not retrieve stock information for validation.");
        return;
      }
      if (parseInt(openStockData.is_active) !== 1) {
        toast.error("This item is currently inactive and cannot be allocated.");
        return;
      }
      if (parseFloat(updatedItem.quantity) > parseFloat(openStockData.open_stock)) {
        toast.error(`Quantity exceeds available open stock (${openStockData.open_stock}).`);
        return;
      }
    } catch (error) {
      console.error('Error fetching open stock during update:', error);
      toast.error("An error occurred while validating stock.");
      return;
    }

    items4.value[editIndex.value].quantity = updatedItem.quantity;
  }
  closeEditModal();
};

const closeEditModal = () => {
  showEditModal.value = false;
  editIndex.value = -1;
};

const deleteItem = (index) => {
  deleteIndex.value = index;
  showDeleteModal.value = true;
};

const confirmDeleteItem = () => {
  if (deleteIndex.value !== null) {
    items4.value.splice(deleteIndex.value, 1);
    deleteIndex.value = null;
    showDeleteModal.value = false;
    refreshAvailableItems();
  }
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  deleteIndex.value = null;
};

const updateStockAllocationData = async () => {
  if (items4.value.length === 0) {
    toast.error("Please add at least one item.");
    return;
  }

  updateLoading.value = true;
  try {
    const payload = {
      sales_type: stockAllocation.value.sales_type,
      profile_id: userCategory.value === 'DISTRIBUTOR BACK OFFICE' ? reportingProfileId.value : profileId.value,
      company_id: localStorage.getItem('company_id'),
      item_id: items4.value.map(item => item.item_id),
      qty: items4.value.map(item => item.quantity),
      stock_allocation_date: currentDate.value,
      is_temp: isTemp.value ? 1 : 0
    };

    if (stockAllocation.value.sales_type === '2') payload.dealer_id = stockAllocation.value.dealer_id;
    else if (stockAllocation.value.sales_type === '1') payload.sales_rep_id = stockAllocation.value.sales_rep_id;

    const response = await updateStockAllocation(route.params.id, payload, localToken.value);
    if (response.data.status === 'success') {
      toast.success(response.data.message);
      showDispatchButton.value = !isTemp.value;
    }
  } catch (error) {
    toast.error('Update failed');
  } finally {
    updateLoading.value = false;
  }
};

const navigateToDispatch = () => {
  router.push('/new-dispatch');
};

onMounted(async () => {
  if (localToken.value) {
    await fetchItems();
    await fetchStockAllocationData();
  }
});
</script>

<style lang="scss" scoped>
/* Scoped styles same as provided in your template */
.container {
  margin: 10px;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 32px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
}

.required { color: #ef4444; margin-left: 2px; font-weight: bold; }

.dropdowns-wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
  align-items: end;
}

.dropdown-item {
  position: relative;
  display: flex;
  flex-direction: column;
  label { font-weight: 700; font-size: 0.85rem; color: #1f2937; margin-bottom: 8px; }
  input, select {
    width: 100%; padding: 10px 14px; border: 1px solid #d1d5db; border-radius: 6px;
    &:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
    &:disabled { background-color: #f9fafb; color: #9ca3af; cursor: not-allowed; }
  }
}

.lock-overlay {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 10; cursor: not-allowed;
}

/* ======================== ITEMS EMPTY STATE ======================== */
.items-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 16px;
  text-align: center;
  color: #9ca3af;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px dashed #e5e7eb;

  .fa {
    font-size: 2.2rem;
    color: #d1d5db;
    margin-bottom: 4px;
  }

  p {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: #6b7280;
  }

  span {
    font-size: 0.8rem;
    color: #9ca3af;
  }
}

.table {
  width: 100%; border-collapse: collapse;
  th, td { padding: 16px 8px; text-align: left; }
  th { font-weight: 700; border-bottom: 2px solid #e5e7eb; }
  td { border-bottom: 1px solid #f3f4f6; }
  .fa-edit { color: #6b7280; cursor: pointer; margin-right: 16px; }
  .fa-trash { color: #ef4444; cursor: pointer; }
}

.button-default { background-color: #3b82f6; color: white; width: 100%; &:hover { background-color: #2563eb; } }
.btn-success { background-color: #10b981; color: white; }
.btn-primary { background-color: #3b82f6; color: white; }
.btn-danger { background-color: #ef4444; color: white; }

.modal-footer { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-top: 32px; }
.modal-footer-right { display: flex; gap: 12px; }

.temp-allocation-check {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #007bff;
  }

  label {
    margin-bottom: 0;
    font-size: 14px;
    font-weight: 600;
    color: #2d3748;
    cursor: pointer;
  }
}

.modal {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(17, 24, 39, 0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;
}

.modal-content-confirmation {
  background-color: #ffffff; padding: 32px; border-radius: 8px; width: 420px; text-align: center; position: relative;
  .close { position: absolute; top: 16px; right: 20px; cursor: pointer; font-size: 1.5rem; }
}
</style>